import React from "react";
import { createPortal } from "react-dom";
import { TransitionGroup } from "react-transition-group";

import { colors } from "@apps/viewer/src/ts/config";

import { config } from "@apps/viewer/src/ts/components/utils/Lightbox/config";
import { Fade } from "@apps/viewer/src/ts/components/utils/Lightbox/Fade";

/**
 * Simple context which can be used to determine if a component has been
 * rendered inside a lightbox.
 */
// tslint:disable-next-line variable-name
export const LightboxContext = React.createContext<boolean>(false);

/**
 * Specification of props required to render <Lightbox />.
 */
export type LightboxProps = {
  /**
   * Specifies if the dropdown should currently be toggled (ie. made visible
   * to the user).
   */
  active?: boolean;

  /**
   * Callback that'll be triggered when the dropdown should be closed, letting
   * the parent properly update it's state.
   */
  onClose(): void;

  children: React.ReactNode;
};

/**
 * The Lightbox utility allows easy injection of the lightboxes in Gyldendal's
 * basic layout with custom content inside.
 */
export class Lightbox extends React.PureComponent<LightboxProps> {
  /**
   * Internal state keeping track of whether or not event listeners are
   * currently subscribed.
   */
  protected isEventListenersSubscribed: boolean = false;

  /**
   * Make sure that event listeners are going to be subscribed, if the
   * lightbox is instantly activated.
   */
  public componentDidMount(): void {
    if (this.props.active) {
      this.subscribeEventListeners();
    }
  }

  /**
   * Subscribe / unsubscribe event listeners when the lightbox is being
   * activated / deactivated.
   */
  public componentDidUpdate(): void {
    if (this.props.active) {
      this.subscribeEventListeners();
    } else {
      this.unsubscribeEventListeners();
    }
  }

  /**
   * Unsubscribe event listeners when the component is being unmounted from
   * the DOM.
   */
  public componentWillUnmount(): void {
    this.unsubscribeEventListeners();
  }

  /**
   * Inject a transition group into the render tree at the position of the
   * Dropdown, so that we're ready to transition it into / out of view when
   * needed.
   */
  public render(): React.ReactPortal {
    return createPortal(
      <TransitionGroup component={React.Fragment}>
        {this.props.active ? this.renderLightbox() : undefined}
      </TransitionGroup>,
      document.getElementById("overlays") as HTMLDivElement
    );
  }

  /**
   * Renders the lightbox, automatically running the enter / exit transition
   * defined.
   */
  protected renderLightbox(): React.JSX.Element {
    return (
      <Fade key="lightbox">
        {(ref) => (
          <>
            <div ref={ref} className="lightbox" role="presentation">
              <div className="lightbox__container">
                <LightboxContext.Provider value={true}>
                  {this.props.children}
                </LightboxContext.Provider>
              </div>

              <div
                className="lightbox__backdrop"
                role="presentation"
                onClick={this.onBackdropClick}
              />
            </div>

            <style jsx>{`
              .lightbox {
                /** Overlap the entire viewport */
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
                top: 0;
                z-index: 1000;

                /** Use flex bod to center the container inside */
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .lightbox__container {
                /** Overlap the backdrop */
                position: relative;
                z-index: 2;

                /** Apply basic styling of the lightbox */
                background: ${colors.white};
                box-shadow: 0 0 25px ${config.dropShadowColor};
              }

              .lightbox__backdrop {
                /** Overlap the entire viewport */
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                top: 0;
                z-index: 1;

                /** Apply background color */
                background: ${config.backdropColor};

                /** Disable cursor inheriting */
                cursor: default;
              }
            `}</style>
          </>
        )}
      </Fade>
    );
  }

  /**
   * Helper that subscribes required event listeners when the lightbox is
   * being activated.
   */
  protected subscribeEventListeners(): void {
    // Bail out if already subscribed
    if (this.isEventListenersSubscribed) {
      return;
    }

    this.isEventListenersSubscribed = true;

    // Bind listeners
    window.addEventListener("keyup", this.onKeyUp);
  }

  /**
   * Helper that sunubscribes all event listeners when the lightbox is
   * being deactivated.
   */
  protected unsubscribeEventListeners(): void {
    // Bail out if no subscribed
    if (!this.isEventListenersSubscribed) {
      return;
    }

    this.isEventListenersSubscribed = false;

    // Bind listeners
    window.removeEventListener("keyup", this.onKeyUp);
  }

  /**
   * Listener that's triggered when the user interacts with the keyboard while
   * the lightbox is activated, auto-closing it if he hits escape.
   */
  protected onKeyUp = (evt: KeyboardEvent) => {
    if (evt.keyCode === 27) {
      this.props.onClose();
    }
  };

  /**
   * Listener that's triggered when the user clicks the backdrop of the layer,
   * making sure that the onClose callback is going to be triggered.
   */
  protected onBackdropClick = (evt: React.MouseEvent<HTMLDivElement>) => {
    this.props.onClose();
    evt.stopPropagation();
  };
}
